import React from 'react';
import { 
  CustomLineChart, CustomDonutChart, CustomAreaChart, CustomBarChart, ConcurrencyChart, ModernGauge, MiniAreaChart, SteppedPointChart, AICircleGauge, GlassmorphicBarChart
} from './ChartComponents';
import { TrendingUp, Users, Zap, Clock, PhoneForwarded, MessageCircle, AlertCircle, Globe, Activity } from 'lucide-react';
import './AnalyticsDashboard.css';
import PhoneNumberHealth from './PhoneNumberHealth';

const AnalyticsDashboard = ({ data }) => {
  const horizontalLabels = [
    { name: 'JK - Dialer vo...', value: 95 },
    { name: 'JK - Dialer - o...', value: 80 },
    { name: 'JK - Dialer vo...', value: 65 },
    { name: 'JK - Dialer 99...', value: 50 },
    { name: 'JK DRE - Ope...', value: 15 },
  ];

  return (
    <div className="analytics-container animate-fade-in">
      {/* Row 1: Dense KPI Grid */}
      <div className="dense-kpi-grid">
        <DenseKpiCard label="Total Dialed" value={data.summary.callCounts.toLocaleString()} trend="↑ 12.5%" trendDir="up" />
        <DenseKpiCard label="Avg Call Duration" value={data.summary.duration} trend="Optimal" trendDir="neutral" />
        <DenseKpiCard label="API Latency" value={data.summary.latency} trend="Fast" trendDir="up" />
        <DenseKpiCard label="Lead to Booking" value={`${data.leadBookingRate[data.leadBookingRate.length - 1]?.value || 0}%`} trend="↑ 4%" trendDir="up" />
        
        <DenseKpiCard label="Show Up Rate" value={`${data.showUpRate[data.showUpRate.length - 1]?.value || 0}%`} trend="Target: 40%" trendDir="neutral" />
        <DenseKpiCard label="Speed to Lead" value={`${data.speedToLead[data.speedToLead.length - 1]?.value || 0}s`} trend="Instant" trendDir="neutral" />
        <DenseKpiCard label="Voicemail Rate" value={`${data.voicemailRate[data.voicemailRate.length - 1]?.value || 0}%`} trend="Stable" trendDir="neutral" />
        <DenseKpiCard label="Avg Dial/Contact" value={`${data.avgDialPerContact[data.avgDialPerContact.length - 1]?.value || 0}`} trend="Optimized" trendDir="neutral" />

        <DenseKpiCard label="Curation Rate" value={`${data.curationRate[data.curationRate.length - 1]?.value || 0}%`} trend="Target: 37%" trendDir="neutral" />
        <DenseKpiCard label="Avg Value/Contact" value={`$${data.avgValuePerContact[data.avgValuePerContact.length - 1]?.value || 0}`} trend="Based on 33% Close" trendDir="neutral" />
        <DenseKpiCard label="Avg Rotations" value={`${data.avgNumberRotations[data.avgNumberRotations.length - 1]?.value || 0}`} trend="Healthy" trendDir="neutral" />
        <DenseKpiCard label="Bad Number Rate" value={`${data.badNumberRate[data.badNumberRate.length - 1]?.value || 0}%`} trend="Healthy" trendDir="neutral" />
      </div>

      {/* Row 2: Concurrency & Health Charts */}
      <div className="charts-grid-3">
        <div className="chart-card">
          <div className="chart-header">
            <h3>System Health</h3>
            <span>Agent success rate</span>
          </div>
          <ModernGauge 
            value={Math.round((data.callSuccessful.find(d => d.name === 'Successful')?.value / data.summary.callCounts) * 100) || 0} 
            label="Success Rate" 
            sublabel="Excellent" 
            color="#3B82F6" 
          />
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <h3>Max Concurrent Load</h3>
            <span>Maximum concurrent calls in timeframe</span>
          </div>
          <div style={{ height: '250px' }}>
            <ConcurrencyChart data={data.concurrencyTimeline} />
          </div>
        </div>
        <div className="chart-card">
          <div className="chart-header">
            <h3>User Sentiment</h3>
            <span>All agents</span>
          </div>
          <CustomDonutChart data={data.userSentiment} />
        </div>
      </div>



      {/* Row 6: Stacked Rates, Phone Metrics, Global AI Performance */}
      <div className="charts-grid-3">
        {/* Left Column: Stacked Rates */}
        <div className="chart-card" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', paddingBottom: '2rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <div className="chart-header" style={{ marginBottom: '1rem' }}>
              <h3>Call Picked Up Rate</h3>
              <span>Last 7 Days Trend</span>
            </div>
            <SteppedPointChart data={data.pickedUpRate} variant="hybrid-amber" height={120} />
          </div>
          <div>
            <div className="chart-header" style={{ marginBottom: '1rem' }}>
              <h3>Call Successful Rate</h3>
              <span>Last 7 Days Trend</span>
            </div>
            <SteppedPointChart data={data.successRate} variant="hybrid-aqua" height={120} />
          </div>
        </div>

        {/* Middle Column: Phone Inbound/outbound */}
        <div className="flex flex-col gap-4">
          <div className="chart-header" style={{ marginBottom: 0 }}>
            <h3>Phone Inbound/outbound</h3>
            <span>Call traffic distribution</span>
          </div>
          <div className="air-quality-grid-exact light" style={{ gap: '1rem' }}>
            <div className="gauge-side-exact">
              <AICircleGauge 
                value={data.contactRatio[data.contactRatio.length - 1]?.value || 0} 
                suffix="%"
                label="Contact Ratio" 
                size={160}
                color="#818CF8"
                gradientColor="#3B82F6"
              />
              <p className="gauge-footer-text light">Call traffic remains stable</p>
            </div>
            
            <div className="stats-side-exact uniform" style={{ gap: '0.75rem' }}>
              <ExactStatItem dotColor="#3B82F6" value={`${data.inboundOutbound.find(d => d.name === 'Inbound')?.value.toLocaleString()}`} label="INBOUND" light />
              <ExactStatItem dotColor="#818CF8" value={`${data.inboundOutbound.find(d => d.name === 'Outbound')?.value.toLocaleString()}`} label="OUTBOUND" light />
              <ExactStatItem dotColor="#3B82F6" value={`${data.pickedUpRate[data.pickedUpRate.length - 1]?.value || 0}%`} label="ANSWERED" light />
              <ExactStatItem dotColor="#EF4444" value={`${Math.floor(data.summary.callCounts * 0.08).toLocaleString()}`} label="MISSED" light />
              <ExactStatItem dotColor="#F59E0B" value={`${data.leadBookingRate[data.leadBookingRate.length - 1]?.value || 0}%`} label="LEAD TO BOOKING" light />
              <ExactStatItem dotColor="#06B6D4" value="12s" label="HOLD TIME" light />
            </div>
          </div>
        </div>

        {/* Right Column: Global AI Performance */}
        <div className="flex flex-col gap-4">
          <div className="chart-header" style={{ marginBottom: 0 }}>
            <h3>Global AI Performance</h3>
            <span>Real-time system health</span>
          </div>
          <div className="air-quality-grid-exact light" style={{ gap: '1rem' }}>
            <div className="gauge-side-exact">
              <AICircleGauge 
                value={97} 
                label="System Health" 
                size={160}
                color="#2DD4BF"
              />
              <p className="gauge-footer-text light">Optimized across all active nodes</p>
            </div>
            
            <div className="stats-side-exact uniform" style={{ gap: '0.75rem' }}>
              <ExactStatItem dotColor="#3B82F6" value="103" label="ACTIVE AGENTS" light />
              <ExactStatItem dotColor="#8B5CF6" value={`${Math.floor((data.concurrencyTimeline[data.concurrencyTimeline.length - 1]?.p1 || 0) * 0.2)}`} label="QUEUE DEPTH" light />
              <ExactStatItem dotColor="#2DD4BF" value={`${data.summary.callCounts.toLocaleString()}`} label="TOTAL DIALED" light />
              <ExactStatItem dotColor="#F59E0B" value={`${data.summary.latency}`} label="API RESPONSE" light />
              <ExactStatItem dotColor="#EF4444" value={`${Math.round((data.callSuccessful.find(d => d.name === 'Successful')?.value / data.summary.callCounts) * 100) || 0}%`} label="SUCCESS RATE" light />
              <ExactStatItem dotColor="#06B6D4" value="3.2k" label="TOKENS/SEC" light />
            </div>
          </div>
        </div>
      </div>

      {/* Row 8: Phone Number Health Monitor */}
      <PhoneNumberHealth />
    </div>
  );
};

const DenseKpiCard = ({ label, value }) => (
  <div className="dense-kpi-card">
    <span className="dense-kpi-value">{value}</span>
    <span className="dense-kpi-label">{label}</span>
  </div>
);

const ExactStatItem = ({ dotColor, value, label, light }) => (
  <div className="exact-stat-item uniform-box">
    <div className="exact-stat-dot" style={{ backgroundColor: dotColor, boxShadow: light ? 'none' : `0 0 10px ${dotColor}` }}></div>
    <div className="exact-stat-content">
      <span className="exact-stat-value" style={{ color: light ? '#111827' : '#FFF' }}>{value}</span>
      <span className="exact-stat-label" style={{ color: light ? '#9CA3AF' : '#9CA3AF' }}>{label}</span>
    </div>
  </div>
);

export default AnalyticsDashboard;
